/**
 * src/shell/agents/services/AgentCostController.ts
 *
 * Token Budgeting, Spend Tracking, and Run-Limit Enforcer for Autonomous Agents.
 */

import { AgentCostBudget } from '../types/gstack-agents-types';

const COST_PER_1K_TOKENS: Record<string, number> = {
  edge_local: 0.0,
  cloud_fast: 0.0008,
  frontier_reasoning: 0.015,
};

export class AgentCostController {
  private static instance: AgentCostController;
  private budgets: Map<string, AgentCostBudget> = new Map();

  private constructor() {}

  public static getInstance(): AgentCostController {
    if (!AgentCostController.instance) {
      AgentCostController.instance = new AgentCostController();
    }
    return AgentCostController.instance;
  }

  public getBudget(userId: string): AgentCostBudget {
    this._resetIfNewDay(userId);
    let budget = this.budgets.get(userId);
    if (!budget) {
      budget = {
        userId,
        dailyBudgetUsd: 10.0,
        dailySpentUsd: 0.0,
        totalTokensUsed: 0,
        maxRunsPerDay: 25,
        runsToday: 0,
        lastResetTimestamp: Date.now(),
      };
      this.budgets.set(userId, budget);
    }
    return budget;
  }

  public checkAllowance(
    userId: string,
    estimatedTokens: number,
    tier: 'edge_local' | 'cloud_fast' | 'frontier_reasoning' = 'cloud_fast'
  ): { allowed: boolean; reason?: string } {
    const budget = this.getBudget(userId);

    if (budget.runsToday >= budget.maxRunsPerDay) {
      return {
        allowed: false,
        reason: `Daily run limit reached (${budget.runsToday}/${budget.maxRunsPerDay} runs).`,
      };
    }

    const estimatedCost = (estimatedTokens / 1000) * (COST_PER_1K_TOKENS[tier] || 0.001);
    if (budget.dailySpentUsd + estimatedCost > budget.dailyBudgetUsd) {
      return {
        allowed: false,
        reason: `Daily spend budget exceeded ($${budget.dailySpentUsd.toFixed(2)} / $${budget.dailyBudgetUsd.toFixed(2)} USD).`,
      };
    }

    return { allowed: true };
  }

  public trackUsage(
    userId: string,
    tokensUsed: number,
    tier: 'edge_local' | 'cloud_fast' | 'frontier_reasoning' = 'cloud_fast'
  ): { costUsd: number; remainingDailyBudgetUsd: number } {
    const budget = this.getBudget(userId);
    const costUsd = (tokensUsed / 1000) * (COST_PER_1K_TOKENS[tier] || 0.001);

    budget.dailySpentUsd += costUsd;
    budget.totalTokensUsed += tokensUsed;
    budget.runsToday += 1;

    return {
      costUsd,
      remainingDailyBudgetUsd: Math.max(0, budget.dailyBudgetUsd - budget.dailySpentUsd),
    };
  }

  private _resetIfNewDay(userId: string): void {
    const budget = this.budgets.get(userId);
    if (!budget) return;

    const oneDayMs = 24 * 60 * 60 * 1000;
    if (Date.now() - budget.lastResetTimestamp > oneDayMs) {
      budget.dailySpentUsd = 0.0;
      budget.runsToday = 0;
      budget.lastResetTimestamp = Date.now();
    }
  }
}

export const agentCostController = AgentCostController.getInstance();
